import {
    Feedback360SettingCard,
    type Feedback360SettingChoice,
} from '@/components/Feedback360SettingCard/Feedback360SettingCard';
import {
    Feedback360SettingsLock,
    type Feedback360SettingsLockValue,
} from '@/constants/review/Feedback360Settings';
import {
    ANONYMOUS_FEEDBACK_MINIMUM_RESPONSES,
    FEEDBACK_PRIVACY_ICONS,
    FeedbackPrivacy,
    type FeedbackPrivacyValue,
} from '@/constants/review/FeedbackPrivacy';
import { useAmelioTranslations } from '@/hooks';

export interface FeedbackPrivacyCardProps {
    value: FeedbackPrivacyValue;
    onChange?: (value: FeedbackPrivacyValue) => void;
    /** Absent while the choice is still open. */
    lock?: Feedback360SettingsLockValue;
    className?: string;
    dataTestId?: string;
}

export const FeedbackPrivacyCard = ({
    value,
    onChange,
    lock,
    className,
    dataTestId = 'feedback-privacy-card',
}: FeedbackPrivacyCardProps) => {
    const { t } = useAmelioTranslations();

    const choices: Feedback360SettingChoice<FeedbackPrivacyValue>[] = [
        {
            value: FeedbackPrivacy.NAMED,
            label: t('feedbackPrivacy.card.named.title'),
            iconName: FEEDBACK_PRIVACY_ICONS[FeedbackPrivacy.NAMED],
            meaning: [
                t('feedbackPrivacy.card.named.nameShown'),
                t('feedbackPrivacy.card.named.resultsFromFirst'),
                t('feedbackPrivacy.card.named.followUp'),
            ],
        },
        {
            value: FeedbackPrivacy.ANONYMOUS,
            label: t('feedbackPrivacy.card.anonymous.title'),
            iconName: FEEDBACK_PRIVACY_ICONS[FeedbackPrivacy.ANONYMOUS],
            meaning: [
                t('feedbackPrivacy.card.anonymous.onlyCountShown'),
                t('feedbackPrivacy.card.anonymous.resultsFromMinimum', {
                    count: ANONYMOUS_FEEDBACK_MINIMUM_RESPONSES,
                }),
            ],
        },
    ];

    const isInherited = lock === Feedback360SettingsLock.INHERITED;

    const lockNotices: Record<Feedback360SettingsLockValue, string | undefined> = {
        [Feedback360SettingsLock.LAUNCHED]: t('feedbackPrivacy.card.lockedNotice'),
        [Feedback360SettingsLock.INHERITED]: undefined,
        [Feedback360SettingsLock.COMPLETED]: t('feedbackPrivacy.card.lockedNotice'),
    };

    return (
        <Feedback360SettingCard
            name={'feedback-privacy'}
            title={t('feedbackPrivacy.card.title')}
            description={t(
                lock ? 'feedbackPrivacy.card.fixedDescription' : 'feedbackPrivacy.card.description',
            )}
            iconName={'security-shield-tick'}
            choices={choices}
            value={value}
            onChange={onChange}
            lockNotice={lock && lockNotices[lock]}
            fixedReason={isInherited ? t('feedbackPrivacy.card.inheritedTooltip') : undefined}
            className={className}
            dataTestId={dataTestId}
        />
    );
};
